import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { DocumentCard } from "@/components/app/DocumentUpload";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

const ENTITY_LABELS = {
  dealer_profile: "Dealer Profile",
  loan_application: "Loan Application",
  note: "Note",
  assignment: "Assignment",
  user: "User",
};

export default function AdminDocuments() {
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState("pending");
  const [entityType, setEntityType] = useState("any");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (status !== "any") params.status = status;
      if (entityType !== "any") params.entity_type = entityType;
      const r = await api.get("/admin/documents", { params });
      setItems(r.data.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status, entityType]);

  const grouped = items.reduce((acc, d) => {
    const k = `${d.entity_type}:${d.entity_id}`;
    (acc[k] = acc[k] || []).push(d);
    return acc;
  }, {});

  return (
    <div className="space-y-4" data-testid="admin-documents">
      <div className="flex flex-wrap items-center gap-3 border-b border-border pb-3">
        <div className="font-overline text-accent">Documents Queue</div>
        <div className="flex-1" />
        <div className="flex gap-2 items-center">
          <span className="text-xs text-muted-foreground">Status</span>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="rounded-none w-[160px] h-9" data-testid="admin-docs-status"><SelectValue /></SelectTrigger>
            <SelectContent className="rounded-none">
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="approved">Approved</SelectItem>
              <SelectItem value="rejected">Rejected</SelectItem>
              <SelectItem value="any">Any</SelectItem>
            </SelectContent>
          </Select>
          <span className="text-xs text-muted-foreground">Entity</span>
          <Select value={entityType} onValueChange={setEntityType}>
            <SelectTrigger className="rounded-none w-[180px] h-9" data-testid="admin-docs-entity"><SelectValue /></SelectTrigger>
            <SelectContent className="rounded-none">
              <SelectItem value="any">All Entities</SelectItem>
              <SelectItem value="dealer_profile">Dealer Profile</SelectItem>
              <SelectItem value="loan_application">Loan Application</SelectItem>
              <SelectItem value="note">Note</SelectItem>
              <SelectItem value="assignment">Assignment</SelectItem>
              <SelectItem value="user">User</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {loading ? (
        <div className="text-muted-foreground text-sm">Loading…</div>
      ) : items.length === 0 ? (
        <div className="border border-dashed border-border p-10 text-center text-muted-foreground"
          data-testid="admin-docs-empty">No documents match this filter.</div>
      ) : (
        Object.entries(grouped).map(([key, docs]) => {
          const [etype, eid] = key.split(":");
          return (
            <div key={key} className="border border-border" data-testid={`admin-docs-group-${etype}-${eid}`}>
              <div className="px-3 py-2 bg-muted/30 border-b border-border flex items-center justify-between">
                <div className="font-overline text-xs">
                  {ENTITY_LABELS[etype] || etype} · <code className="font-mono-data text-[10px]">{eid}</code>
                </div>
                <div className="text-[10px] font-mono-data text-muted-foreground">{docs.length} file{docs.length === 1 ? "" : "s"}</div>
              </div>
              <div className="p-2 space-y-2">
                {docs.map((d) => <DocumentCard key={d.document_id} doc={d} isAdmin onChanged={load} />)}
              </div>
            </div>
          );
        })
      )}
    </div>
  );
}
